/**
 * May this request's destination receive a token?
 *
 * The hosted client and this computer may. Any other origin must be a public
 * route — a custom domain or a `*.zerops.app` subdomain — of a project in an
 * organization the user is an active member of. Every read is made with the
 * user's own session, so a project counts only if the user can read it.
 */
import { ClientUserStatuses } from '@zerops/models/client-user';
import { AuthorizeAppRequest, describeHandoverDestination } from './z3-handover.util';

export interface HandoverReader {
  /** `GET /user/info`, every membership whatever its status. */
  memberships(): Promise<Array<{ clientId: string; status: ClientUserStatuses; client?: { accountName?: string }; }>>;
  projects(clientId: string): Promise<Array<{ id: string; name: string; }>>;
  /** The domains of a project's public routes. */
  routedDomains(projectId: string): Promise<Array<{ domainName: string; sslStatus: string; }>>;
}

export type HandoverCheck =
  | { allowed: true; project?: string; organization?: string; }
  | { allowed: false; reason: 'not_a_route' | 'check_failed'; };

const CHECK_FAILED: HandoverCheck = { allowed: false, reason: 'check_failed' };

const fulfilled = <T>(results: PromiseSettledResult<T>[]): T[] => results
  .flatMap((result) => result.status === 'fulfilled' ? [ result.value ] : []);

export const checkHandoverDestination = async (
  request: AuthorizeAppRequest,
  reader: HandoverReader
): Promise<HandoverCheck> => {
  const { origin, needsCheck } = describeHandoverDestination(request);

  if (!needsCheck) {
    return { allowed: true };
  }
  const memberships = await reader.memberships().catch(() => null);
  if (!memberships) {
    return CHECK_FAILED;
  }

  const host = new URL(origin).host;

  const organizations = memberships.filter(({ status }) => status === ClientUserStatuses.active);
  const searches = await Promise.allSettled(organizations.map(async (organization) =>
    (await reader.projects(organization.clientId)).map((project) => ({ project, organization }))
  ));
  const routes = await Promise.allSettled(fulfilled(searches).flat().map(async (candidate) =>
    ({ ...candidate, domains: await reader.routedDomains(candidate.project.id) })
  ));

  // An active certificate means the domain's DNS already points at Zerops,
  // not merely that a member typed it into the project.
  const match = fulfilled(routes).find(({ domains }) => domains
    .some(({ domainName, sslStatus }) => domainName === host && sslStatus === 'ACTIVE'));
  if (match) {
    return { allowed: true, project: match.project.name, organization: match.organization.client?.accountName };
  }
  // "Not a route" only when every read succeeded; otherwise the route may sit
  // in what could not be read.
  const failed = [ ...searches, ...routes ].some(({ status }) => status === 'rejected');
  return failed ? CHECK_FAILED : { allowed: false, reason: 'not_a_route' };
};
